"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Globe2,
  Sparkles,
  Target,
  Building2,
  Layers,
  ArrowRight,
  Crown,
  Mic,
  Activity,
  Shield,
  Compass,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "18", suffix: "", label: "Countries Active", sub: "Three continents", accent: "denim" },
  { metric: "30", suffix: "+", label: "Nations Served", sub: "Sovereign client base", accent: "emerald" },
  { metric: "15", suffix: "+", label: "Languages", sub: "Operational coverage", accent: "purple" },
  { metric: "0", suffix: "", label: "Security Breaches", sub: "15+ years clean record", accent: "amber-gold" },
];

const STAGES = [
  { num: "01", title: "Intelligence", body: "Every global engagement begins with intelligence gathering. The intelligence engine monitors 200+ platforms across 50+ languages in 195 countries. The ground truth is discovered before any strategy is formed.", accent: "red" },
  { num: "02", title: "Analysis", body: "Pattern identification, anomaly detection, correlation mapping — conducted across jurisdictions, languages, and cultural contexts. The clinical method applied to cross-border data: the symptom is never the disease.", accent: "purple" },
  { num: "03", title: "Strategy", body: "The Penta P framework is deployed. Each pillar assigned its role, each jurisdiction calibrated to its cultural and regulatory context. Strategy is an operational plan driving coordinated action across borders.", accent: "cyan" },
  { num: "04", title: "Orchestration", body: "The neural command interface coordinates all platforms simultaneously across every jurisdiction, reducing decision time from 24-72 hours down to under one hour with 95% coordination success.", accent: "emerald" },
  { num: "05", title: "Amplification", body: "Content deploys at scale with platform-specific and jurisdiction-specific optimization. Ninety-five to ninety-eight percent organic authenticity across every cultural context.", accent: "amber-gold" },
  { num: "06", title: "Feedback", body: "Continuous monitoring and outcome measurement flow back into the intelligence stage — across every jurisdiction simultaneously. The loop never closes. It accelerates.", accent: "denim" },
];

const DIMENSIONS = [
  { title: "Language Infrastructure", body: "Fifteen-plus languages in operational coverage. Native-grade execution across linguistic regions. Language is not merely a communication tool — it is a cultural operating system. Nuance cannot be faithfully rendered without deep cultural understanding." },
  { title: "Cultural Protocol Architecture", body: "Every jurisdiction operates according to distinct cultural protocols. How decisions are made, how authority is communicated, how trust is established. The operator who does not understand these distinctions will produce technically correct work that fails culturally." },
  { title: "Trust-Building Discipline", body: "Sovereign-scale clients do not grant trust based on capability alone. Trust is earned through discretion, through demonstrated understanding of cultural context, and through the absolute protection of confidential information." },
  { title: "Regulatory Navigation", body: "Every jurisdiction operates under distinct regulatory frameworks. Operating across these requires not just legal awareness but operational architecture that adapts to each jurisdiction while maintaining the integrity of the unified system." },
];

const SECTORS = [
  { title: "Government & Sovereign", body: "Citizen perception, secure communications, policy visibility, governance digitization. All five pillars activated simultaneously.", accent: "red" },
  { title: "Presidential & Prime Ministerial Offices", body: "Secure executive communications, real-time threat intelligence, national perception. Sovereign-grade discretion.", accent: "purple" },
  { title: "Kingdoms & Royal Families", body: "Reputation protection, privacy enforcement, legacy narrative management with absolute discretion.", accent: "cyan" },
  { title: "Political Organizations & Candidates", body: "Voter intelligence, narrative control, ground operations, campaign coordination. All five pillars activated.", accent: "emerald" },
  { title: "Corporate & Enterprise", body: "Brand protection, crisis response, competitive positioning, secure communications for Fortune-scale corporations.", accent: "amber-gold" },
  { title: "Defense & Intelligence", body: "Secure communications, threat detection, intelligence fusion, cyber forensics for sovereign clients.", accent: "denim" },
];

export default function GlobalOperationsPage() {
  return (
    <>
      <PageHero
        icon={Globe2}
        overline="Global Operations"
        overlineClassName="text-denim"
        title={
          <>
            Sovereign-Scale Execution Across <span className="text-gradient-premium">Continents.</span>
          </>
        }
        subtitle="The world&apos;s most consequential operations do not happen in boardrooms. They happen across borders — in the space between jurisdictions, cultures, languages, and sovereign interests, where the margin for error is measured in national stability. Dr. Jyoti Kush operates in this space."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Global Operations" }]}
        badge="Eighteen countries. One unified command."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
              <Sparkles className="size-3.5 text-denim" />
              <span className="text-overline text-denim">The Global Footprint</span>
            </div>
            <h2 className="text-display-md mb-5">
              Where stakes are national and the margin for error is zero.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              When the client is a government, the consequence of operational failure is not a lost contract — it is a national consequence. When the client is a royal household, the consequence is a legacy threatened. When the client is a defense agency, the consequence is a security breach with cascading implications.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Dr. Jyoti Kush has operated at this altitude for fifteen-plus years. She has overseen engagements across eighteen countries where the margin for error was measured in national stability. She has managed operations where the confidentiality was governed by national security level NDAs.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Six-Stage Methodology at Global Scale</span>
          </div>
          <h2 className="text-display-md mb-4">How Dr. Jyoti Kush orchestrates cross-border operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every engagement — regardless of jurisdiction, cultural complexity, or operational stakes — follows the same battle-tested operating system.
          </p>
        </div>
        <div className="space-y-4">
          {STAGES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring}`}>
                    <span className="text-display-sm font-bold text-white">{s.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Compass className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Cultural Dimension</span>
          </div>
          <h2 className="text-display-md mb-4">Adapting across cultures and languages.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Operating across eighteen countries is not a logistics challenge. It is a cultural intelligence challenge. Cultural adaptation is infrastructure, not courtesy.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {DIMENSIONS.map((d, i) => {
            const accents = ["red", "purple", "cyan", "emerald"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{d.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Eight Sectors Served</span>
          </div>
          <h2 className="text-display-md mb-4">Sovereign-scale operations across every sector that matters.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The global operations infrastructure serves a selective top-tier clientele across eight major sectors. Each sector presents distinct challenges addressed through tailored platform capabilities.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTORS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="global-operations-tree" ariaLabel="Global Operations Tree">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Globe2 className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Global Operations Tree</span>
          </div>
          <h2 className="text-display-md mb-4">Four roots. One sovereign-scale execution system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature compilation. Four root operational domains, each branching into capabilities refined across sovereign-scale engagements. The tree is the architecture of global operations.
          </p>
        </div>
        <TreeBlock
          entries={[
            {
              id: "root-1",
              label: "Operational Footprint",
              body: "Eighteen countries, three continents, thirty-plus nations served. The geographic and operational base from which every global engagement is executed.",
              accent: "denim",
              children: [
                { id: "of-1", label: "Regional Hubs", body: "Operational presence in three continental hubs. Local teams with global command integration. The geography of execution.", accent: "denim" },
                { id: "of-2", label: "Cross-Border Coordination", body: "Twenty-four hour operational cycle. The sun never sets on the engagement. The neural command interface operates continuously across time zones.", accent: "denim" },
                { id: "of-3", label: "Local Field Teams", body: "Booth-level teams in seventeen hundred-plus constituencies. Field operatives trained, equipped, and coordinated from the central command.", accent: "denim" },
              ],
            },
            {
              id: "root-2",
              label: "Cultural and Linguistic Reach",
              body: "Fifteen-plus languages in operational coverage. Cultural protocol architecture that enables authentic engagement across every jurisdiction.",
              accent: "purple",
              children: [
                { id: "cl-1", label: "Native-Grade Execution", body: "Fifty-plus language capability. Cultural calibration embedded in every output. Native voice in every channel.", accent: "purple" },
                { id: "cl-2", label: "Cultural Protocol Architecture", body: "Distinct cultural protocols per jurisdiction. How decisions are made, how authority is communicated, how trust is established. The discipline of cultural intelligence.", accent: "purple" },
              ],
            },
            {
              id: "root-3",
              label: "Resilience Architecture",
              body: "Operating through every global disruption. Pandemic. Supply chain collapse. Geopolitical rupture. Market volatility. The infrastructure that does not stop.",
              accent: "emerald",
              children: [
                { id: "ra-1", label: "Disruption Continuity", body: "The organization grew, excelled, and evolved during the COVID era while most organizations were suffering. The system that does not stop is the system that scales.", accent: "emerald" },
                { id: "ra-2", label: "Air-Gap Capability", body: "Operations continue without internet exposure when required by threat environment. Sovereign-grade resilience by design.", accent: "emerald" },
                { id: "ra-3", label: "Multi-Jurisdictional Redundancy", body: "Operational redundancy across jurisdictions. No single point of failure. The system continues when any single location is compromised.", accent: "emerald" },
              ],
            },
            {
              id: "root-4",
              label: "Confidentiality and Trust",
              body: "National security level NDAs. The trust protocol that enables sovereign clients to engage on the most sensitive operations.",
              accent: "amber-gold",
              children: [
                { id: "ct-1", label: "National Security NDAs", body: "Confidentiality at the standard sovereign clients require. The trust protocol that enables engagement on the most sensitive matters.", accent: "amber-gold" },
                { id: "ct-2", label: "Discretion Doctrine", body: "The most consequential work is done without an audience. Visibility is a choice, not a requirement. Outcomes are the currency of credibility.", accent: "amber-gold" },
                { id: "ct-3", label: "Sovereign Security Posture", body: "Zero-trust, post-quantum cryptography, military-grade encryption. The security architecture that protects every engagement.", accent: "amber-gold" },
              ],
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Cross-Border Operating Doctrine</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles for sovereign-scale global execution.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The principles that distinguish operations that scale across borders from operations that fail at the first cultural boundary. The doctrine applied to every cross-border engagement.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Cultural Intelligence First", body: "Cultural adaptation is infrastructure, not courtesy. The operator who does not understand the cultural protocol will produce technically correct work that fails culturally. The first investment is in cultural understanding.", icon: <Globe2 className="size-5" />, accent: "red" },
            { title: "Local Presence, Global Command", body: "Local field teams in seventeen hundred-plus constituencies. The neural command interface coordinates from the center. The combination is the capability — local authenticity, global discipline.", icon: <Building2 className="size-5" />, accent: "purple" },
            { title: "Time Zone as Advantage", body: "Twenty-four hour operational cycle. The sun never sets on the engagement. The capability that turns a logistical challenge into a strategic advantage — overnight intelligence, overnight response.", icon: <Activity className="size-5" />, accent: "cyan" },
            { title: "Regulatory Architecture", body: "Every jurisdiction operates under distinct regulatory frameworks. The architecture that adapts to each while maintaining unified system integrity. The capability that no point-solution vendor can replicate.", icon: <Shield className="size-5" />, accent: "emerald" },
            { title: "Trust-Building Discipline", body: "Sovereign-scale clients do not grant trust based on capability alone. Trust is earned through discretion, through demonstrated cultural understanding, and through the absolute protection of confidential information.", icon: <Compass className="size-5" />, accent: "amber-gold" },
            { title: "Resilience by Design", body: "The system that operates through every global disruption. Built in-house. Zero third-party dependencies. Sovereign-grade security at every layer. The architecture of operational durability.", icon: <Target className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Global Operations Record</span>
          </div>
          <h2 className="text-display-md mb-4">Eighteen countries. Fifteen-plus years. One record.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data from sovereign-scale global engagements. The record is the verified performance of an organization that has been operating at this altitude for over a decade.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 18, label: "Countries Active", description: "Three continents, one unified command", accent: "denim" },
            { value: 30, suffix: "+", label: "Nations Served", description: "Sovereign client base", accent: "emerald" },
            { value: 15, suffix: "+", label: "Languages", description: "Operational coverage", accent: "purple" },
            { value: 0, label: "Security Breaches", description: "Fifteen-plus years clean record", accent: "amber-gold" },
            { value: 195, label: "Countries Monitored", description: "Intelligence architecture reach", accent: "red" },
            { value: 50, suffix: "+", label: "Languages Processed", description: "Multi-language operational capability", accent: "cyan" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage sovereign-scale global operations.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Sovereign-grade execution across continents. National security level confidentiality. Every engagement begins with diagnosis. Every outcome verified.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Globe2, accent: "denim" },
          { title: "Privacy & Information Security", description: "Sovereign-grade protection. Military-grade encryption, zero trust, zero breaches.", href: "/expertise/privacy-information-security/", icon: Shield, accent: "emerald" },
          { title: "Entrepreneurship & Building", description: "The discipline of constructing consequential organizations that outlast their founders.", href: "/expertise/entrepreneurship/", icon: TrendingUp, accent: "purple" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
